import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box, Card, CardContent, Grid, Typography,
} from '@mui/material';

/**
 * Server-side aggregate counts returned alongside an itemProgressSearch page.
 * Mirrors the GraphQL `ItemProgressAggregates` type and is stored as opaque
 * JSON on the cached list; we accept any shape and defensively coerce.
 */
interface ItemProgressAggregates {
  totalRows?: number | null;
  completedCount?: number | null;
  inProgressCount?: number | null;
  notStartedCount?: number | null;
  averageProgress?: number | null;
  certifiedCount?: number | null;
}

interface SummaryTile {
  label: string;
  value: string;
  testId: string;
}

const formatInt = (n: number | null | undefined): string => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Math.round(Number(n)).toLocaleString();
};

const formatPercent = (n: number | null | undefined): string => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${Math.round(Number(n))}%`;
};

const buildEngagementTiles = (a: ItemProgressAggregates): SummaryTile[] => [
  { label: 'Total', value: formatInt(a.totalRows), testId: 'tile-total' },
  { label: 'Completed', value: formatInt(a.completedCount), testId: 'tile-completed' },
  { label: 'In progress', value: formatInt(a.inProgressCount), testId: 'tile-in-progress' },
  { label: 'Not started', value: formatInt(a.notStartedCount), testId: 'tile-not-started' },
  { label: 'Avg progress', value: formatPercent(a.averageProgress), testId: 'tile-avg-progress' },
];

const buildComplianceTiles = (a: ItemProgressAggregates): SummaryTile[] => [
  { label: 'Total', value: formatInt(a.totalRows), testId: 'tile-total' },
  { label: 'Certified', value: formatInt(a.certifiedCount), testId: 'tile-certified' },
  { label: 'In progress', value: formatInt(a.inProgressCount), testId: 'tile-in-progress' },
  { label: 'Not started', value: formatInt(a.notStartedCount), testId: 'tile-not-started' },
];

interface ItemProgressSummaryTilesProps {
  /**
   * MobX cached list (`ICachedList`); we read `aggregates` from it. Typed loose
   * to avoid pulling MobX-State-Tree generics into this leaf component.
   */
  cachedList: { aggregates?: ItemProgressAggregates | null } | null | undefined;
  /** Tile preset; defaults to `engagement`. */
  variant?: 'engagement' | 'compliance';
}

/**
 * Compact row of summary tiles backed by `itemProgressSearch.aggregates`.
 *
 * Renders nothing when the cached list has no aggregates yet (legacy Dynamo
 * backend or pre-load), so it is always safe to drop above the table.
 */
const ItemProgressSummaryTilesInner: React.FC<ItemProgressSummaryTilesProps> = ({
  cachedList,
  variant = 'engagement',
}) => {
  const aggregates = cachedList?.aggregates;
  if (!aggregates) return null;

  const tiles = variant === 'compliance'
    ? buildComplianceTiles(aggregates)
    : buildEngagementTiles(aggregates);

  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2}>
        {tiles.map((tile) => (
          <Grid key={tile.testId} item xs={6} sm={4} md={2.4}>
            <Card variant="outlined" data-testid={tile.testId}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">
                  {tile.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {tile.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default observer(ItemProgressSummaryTilesInner);
